import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useEventListener } from '@vueuse/core'

/**
 * Composable for managing global search functionality
 * @returns Object containing search state and methods
 */
export function useSearch() {
  const searchIsOpen = ref(false)

  /**
   * Detects if the user is on a Mac device
   */
  const isMac = computed(() => /Mac|iPod|iPhone|iPad/.test(window.navigator.userAgent))

  /**
   * Toggles the search panel open or closed
   * @param action - Optional action: 'open' to open, 'close' to close, or null to toggle
   */
  function toggleSearchPanel(action: 'open' | 'close' | null = null): void {
    if (action !== null) {
      if (action === 'open') {
        searchIsOpen.value = true
      } else {
        searchIsOpen.value = false
      }
    } else {
      searchIsOpen.value = !searchIsOpen.value
    }
  }

  /**
   * Closes the search panel
   */
  function closeSearch(): void {
    searchIsOpen.value = false
  }

  /**
   * Handles keyboard shortcut (Cmd+K on Mac, Ctrl+K on Windows/Linux)
   */
  function onKeydown(event: KeyboardEvent): void {
    const modifier = isMac.value ? 'metaKey' : 'ctrlKey'
    const cmdK = event[modifier as keyof KeyboardEvent] && event.key.toLowerCase() === 'k'

    if (cmdK) {
      event.preventDefault()
      toggleSearchPanel()
    }
  }

  // Set up keyboard event listener
  useEventListener(document, 'keydown', onKeydown)

  return {
    searchIsOpen,
    isMac,
    toggleSearchPanel,
    closeSearch
  }
}

